import { getBaseThemeName } from "@/features/branding";
import { PortfolioExportAssetLevelDownloadButton, SummaryStatusModal, SummaryWithProgress } from "@/features/pe_aldd";
import type { AdaptationSummaryWithProgress } from "@/features/pe_aldd/schema";
import { useGetCurrentUserQuery } from "@/store/services/supabase";
import { AnalysisCollectionEditModal } from "@/tools/aggregate/common/AnalysisCollectionEditModal";
import type { PortfolioNewRunModalStatus } from "@/tools/aggregate/portfolio-export/types";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { autoAnalysedResilienceTypeValues } from "@/types/adaptation";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useDebouncedCallback } from "@mantine/hooks";
import { Modal, notification } from "antd";
import { format, subMinutes } from "date-fns";
import { createContext, PropsWithChildren, ReactNode, useContext, useEffect, useRef, useState, type MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
const emptyRefObj: MutableRefObject<null> = {
  current: null
};
const PORTFOLIO_TUTORIAL_ORDER = ["landing", "listFilters", "startNewRun", "typeSelection", "downloadTemplate", "customiseTemplate", "uploadCsv", "reviewInfo", "advancedSettings", "usage", "runAnalysis", "processingAlert", "alertBell", "adaptationsRunning", "completedItem", "summaryQueue", "summaryGenerated", "downloadAggregated", "downloadPointLevel", "outputTypeModal", "downloadMetadata", "moreActions", "editModal", "deleteModal", "viewResults"] as const;
export type TutorialStepId = (typeof PORTFOLIO_TUTORIAL_ORDER)[number];
const isAtOrAfter = (stepId: TutorialStepId, target: TutorialStepId) => PORTFOLIO_TUTORIAL_ORDER.indexOf(stepId) >= PORTFOLIO_TUTORIAL_ORDER.indexOf(target);
const TUTORIAL_ASSET_COUNT = 3;
const TUTORIAL_ADAPTATION_COUNT = TUTORIAL_ASSET_COUNT * autoAnalysedResilienceTypeValues.length;
/** Non-zero so the running step shows progress rather than a stalled 0%. */
const TUTORIAL_ADAPTATIONS_PROCESSED = 3;
const assetsProcessing = {
  errorCount: 0,
  totalCount: TUTORIAL_ASSET_COUNT,
  processedCount: 0,
  unprocessedCount: TUTORIAL_ASSET_COUNT
};
const assetsComplete = {
  errorCount: 0,
  totalCount: TUTORIAL_ASSET_COUNT,
  processedCount: TUTORIAL_ASSET_COUNT,
  unprocessedCount: 0
};
const adaptationsProcessing = {
  errorCount: 0,
  totalCount: TUTORIAL_ADAPTATION_COUNT,
  processedCount: TUTORIAL_ADAPTATIONS_PROCESSED,
  unprocessedCount: TUTORIAL_ADAPTATION_COUNT - TUTORIAL_ADAPTATIONS_PROCESSED
};
const adaptationsComplete = {
  errorCount: 0,
  totalCount: TUTORIAL_ADAPTATION_COUNT,
  processedCount: TUTORIAL_ADAPTATION_COUNT,
  unprocessedCount: 0
};
const getTutorialAdaptationStateForStep = (stepId: TutorialStepId, canRunAutoAdaptation: boolean): Pick<PortfolioExportJobSchema, "autoAdaptationStatus" | "adaptations"> => {
  if (!canRunAutoAdaptation || !isAtOrAfter(stepId, "adaptationsRunning")) return {
    autoAdaptationStatus: null,
    adaptations: undefined
  };
  return stepId === "adaptationsRunning" ? {
    autoAdaptationStatus: "pending",
    adaptations: adaptationsProcessing
  } : {
    autoAdaptationStatus: "completed",
    adaptations: adaptationsComplete
  };
};
export const getTutorialSchemaForStep = (base: PortfolioExportJobSchema, stepId: TutorialStepId, canRunAutoAdaptation: boolean): PortfolioExportJobSchema => {
  const assetsDone = stepId === "adaptationsRunning" || isAtOrAfter(stepId, "completedItem");
  const analysisComplete = isAtOrAfter(stepId, "completedItem");
  const summaryGenerated = isAtOrAfter(stepId, "downloadAggregated");
  return {
    ...base,
    assets: assetsDone ? assetsComplete : assetsProcessing,
    status: analysisComplete ? "completed" : "processing",
    ...getTutorialAdaptationStateForStep(stepId, canRunAutoAdaptation),
    summaryId: summaryGenerated ? `${base.id}-summary-id` : "",
    isSummaryGenerated: summaryGenerated
  };
};
interface PortfolioTutorialState {
  isTutorialOpen: boolean;
  canRunAutoAdaptation: boolean;
  isTutorialNewRunModalOpen: boolean;
  setIsTutorialNewRunModalOpen: (value: boolean) => void;
  tutorialNewRunState: PortfolioNewRunModalStatus;
  setTutorialNewRunState: (value: PortfolioNewRunModalStatus) => void;
  tutorialPortfolioSchema: PortfolioExportJobSchema;
  setTutorialPortfolioSchema: (value: PortfolioExportJobSchema) => void;
  setTutorialSchemaForStep: (stepId: TutorialStepId) => void;
  newRunModalTrigger: MutableRefObject<null>;
  newRunModalTypeSelection: MutableRefObject<null>;
  newRunModalUpload: {
    uploadNewRun: MutableRefObject<null>;
    customiseTrigger: MutableRefObject<null>;
    csvUpload: MutableRefObject<null>;
  };
  newRunModalReview: {
    info: MutableRefObject<null>;
    advancedSettings: MutableRefObject<null>;
    usage: MutableRefObject<null>;
    runAnalysisTrigger: MutableRefObject<null>;
  };
  analysisRun: {
    alert: MutableRefObject<null>;
    item: MutableRefObject<null>;
  };
  portfolioItem: {
    actionsMenu: MutableRefObject<null>;
    downloadsMenu: MutableRefObject<null>;
    viewResultsTrigger: MutableRefObject<null>;
  };
  /** The portalled download menu, published once it has mounted so the tour can highlight it. */
  downloadsMenuPopup: MutableRefObject<null>;
  outputTypeModal: MutableRefObject<null>;
  editModal: MutableRefObject<null>;
  deleteModal: MutableRefObject<null>;
  showDownloadDropdown: boolean;
  setShowDownloadDropdown: (value: boolean) => void;
  selectedDownloadItemIndex: number | undefined;
  setSelectedDownloadItemIndex: (value: number | undefined) => void;
  setShowSummaryModal: (value: boolean) => void;
  handleSummaryStep: () => void;
  handleSummaryGenerated: () => void;
  summaryQueueModal: MutableRefObject<null>;
  showPointLevelResultsModal: boolean;
  setShowPointLevelResultsModal: (value: boolean) => void;
  showMoreDropdown: boolean;
  setShowMoreDropdown: (value: boolean) => void;
  showTutorialEditModal: boolean;
  setShowTutorialEditModal: (value: boolean) => void;
  showTutorialDeleteModal: boolean;
  setShowTutorialDeleteModal: (value: boolean) => void;
  alertBellTrigger: MutableRefObject<null>;
  listFilters: MutableRefObject<null>;
}
const PortfolioTutorialContext = createContext<PortfolioTutorialState | undefined>(undefined);
export const PortfolioTutorialProvider = ({
  isTutorialOpen,
  canRunAutoAdaptation,
  isLargeSite,
  children
}: PropsWithChildren & {
  isTutorialOpen: boolean;
  canRunAutoAdaptation: boolean;
  isLargeSite: boolean;
}) => {
  const {
    data: currentUser
  } = useGetCurrentUserQuery();
  const {
    t: terms
  } = useTranslation("terms");
  const showAdaptationSummary = canRunAutoAdaptation && !isLargeSite;
  const tutorialOpenedAt = new Date();
  const inQueueSummaryCreatedAt = subMinutes(tutorialOpenedAt, 3);
  const availableHazards: any[] = [];
  const initialTutorialPortfolioName = getBaseThemeName() === "hkma" ? "Portfolio HK-locations-3_new" : "Portfolio Example - 3_new_locations";
  const initialTutorialPortfolioSchema: PortfolioExportJobSchema = {
    id: "tutorial-schema-id",
    name: initialTutorialPortfolioName,
    assets: {
      errorCount: 0,
      totalCount: 3,
      processedCount: 0,
      unprocessedCount: 3
    },
    startTime: tutorialOpenedAt.getTime(),
    status: "processing",
    summaryId: "",
    userId: currentUser?.id ?? "",
    email: currentUser?.email ?? "",
    rerunEmail: null,
    rerunAt: null,
    groupId: currentUser?.group_id ?? "",
    groupName: currentUser?.group_name ?? "",
    secret: undefined as any,
    isLargeSite: false,
    isPrivate: false,
    isSummaryGenerated: false,
    stamps: [format(tutorialOpenedAt, "yyyy-MM-dd")],
    incorporatedHazards: availableHazards,
    metadata: undefined,
    regionType: undefined,
    autoAdaptationStatus: null,
    collectionVersion: null,
    updateAvailable: false,
    adaptations: undefined
  };
  const tutorialSummaries: SummaryWithProgress[] = [{
    id: "tutorial-schema-id-queue",
    name: getBaseThemeName() === "hkma" ? "Portfolio HK-locations" : "Portfolio Example - locations",
    is_private: false,
    user_id: currentUser?.id ?? "",
    email: currentUser?.email ?? "",
    group_id: currentUser?.group_id ?? "",
    group_name: currentUser?.group_name ?? "",
    total: 9,
    success: 9,
    error: 0,
    pending: 0,
    summary_id: null,
    summary_started_at: inQueueSummaryCreatedAt.toISOString(),
    is_summary_generated: false,
    secret_enc: "",
    unprocessed: 1,
    created_at: inQueueSummaryCreatedAt.toISOString(),
    deleted_at: null,
    stamps: [format(inQueueSummaryCreatedAt, "yyyy-MM-dd")],
    incorporated_hazards: availableHazards,
    metadata: null,
    status: "pending",
    position: 1,
    region_type: null,
    rerun_at: null,
    rerun_email: null,
    auto_adaptation_status: null,
    collection_version: null,
    update_available: false
  }, {
    id: "tutorial-schema-id",
    name: initialTutorialPortfolioName,
    is_private: false,
    user_id: currentUser?.id ?? "",
    email: currentUser?.email ?? "",
    group_id: currentUser?.group_id ?? "",
    group_name: currentUser?.group_name ?? "",
    total: 3,
    success: 3,
    error: 0,
    pending: 0,
    summary_id: null,
    summary_started_at: tutorialOpenedAt.toISOString(),
    is_summary_generated: false,
    secret_enc: "",
    unprocessed: 3,
    created_at: tutorialOpenedAt.toISOString(),
    deleted_at: null,
    stamps: [format(tutorialOpenedAt, "yyyy-MM-dd")],
    incorporated_hazards: availableHazards,
    metadata: null,
    status: "pending",
    position: 2,
    region_type: null,
    rerun_at: null,
    rerun_email: null,
    auto_adaptation_status: null,
    collection_version: null,
    update_available: false
  }];
  const tutorialAdaptationSummaries: AdaptationSummaryWithProgress[] = showAdaptationSummary ? [{
    id: "tutorial-schema-id-adaptation-summary",
    name: initialTutorialPortfolioName,
    user_id: currentUser?.id ?? "",
    group_name: null,
    success: TUTORIAL_ASSET_COUNT,
    error: 0,
    summary_id: null,
    summary_started_at: tutorialOpenedAt.toISOString(),
    status: "pending",
    position: 3
  }] : [];
  const [tutorialPortfolioSchema, setTutorialPortfolioSchema] = useState<PortfolioExportJobSchema>(initialTutorialPortfolioSchema);
  const setTutorialSchemaForStep = (stepId: TutorialStepId) => setTutorialPortfolioSchema(getTutorialSchemaForStep(initialTutorialPortfolioSchema, stepId, canRunAutoAdaptation));
  const [isTutorialNewRunModalOpen, setIsTutorialNewRunModalOpen] = useState(false);
  const [tutorialNewRunState, setTutorialNewRunState] = useState<PortfolioNewRunModalStatus>("INITIAL");
  const newRunModalTrigger = useRef(null);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [selectedDownloadItemIndex, setSelectedDownloadItemIndex] = useState<number | undefined>(undefined);
  const downloadsMenuPopupRef = useRef(null);
  const [downloadsMenuPopup, setDownloadsMenuPopup] = useState(emptyRefObj);
  const initDownloadsMenuPopup = useDebouncedCallback(() => setDownloadsMenuPopup(downloadsMenuPopupRef), {
    delay: 600,
    flushOnUnmount: true
  });
  useEffect(() => {
    if (showDownloadDropdown) {
      initDownloadsMenuPopup();
    } else {
      setDownloadsMenuPopup(emptyRefObj);
    }
  }, [showDownloadDropdown]);
  const [showTutorialEditModal, setShowTutorialEditModal] = useState(false);
  const [showTutorialDeleteModal, setShowTutorialDeleteModal] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [portfolioItem, setPortfolioItem] = useState({
    actionsMenu: emptyRefObj,
    downloadsMenu: emptyRefObj,
    viewResultsTrigger: emptyRefObj
  });
  const actionsMenuRef = useRef(null);
  const downloadsMenuRef = useRef(null);
  const viewResultsTriggerRef = useRef(null);
  const initPortfolioItem = useDebouncedCallback(() => {
    setPortfolioItem({
      actionsMenu: actionsMenuRef,
      downloadsMenu: downloadsMenuRef,
      viewResultsTrigger: viewResultsTriggerRef
    });
  }, {
    delay: 300,
    flushOnUnmount: true
  });
  const editModalRef = useRef(null);
  const [editModal, setEditModal] = useState(emptyRefObj);
  const initEditModal = useDebouncedCallback(() => setEditModal(editModalRef), {
    delay: 300,
    flushOnUnmount: true
  });
  useEffect(() => {
    if (showTutorialEditModal) initEditModal();
  }, [showTutorialEditModal]);
  const deleteModalRef = useRef(null);
  const [deleteModal, setDeleteModal] = useState(emptyRefObj);
  useEffect(() => {
    if (showTutorialDeleteModal) {
      setDeleteModal(deleteModalRef);
    }
  }, [showTutorialDeleteModal]);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showPointLevelResultsModal, setShowPointLevelResultsModal] = useState(false);
  const outputTypeModalRef = useRef(null);
  const [outputTypeModal, setOutputTypeModal] = useState(emptyRefObj);
  const handlePointLevelButton = useDebouncedCallback(() => {
    setOutputTypeModal(outputTypeModalRef);
  }, {
    delay: 300,
    flushOnUnmount: true
  });
  useEffect(() => {
    if (showPointLevelResultsModal) {
      handlePointLevelButton();
    } else {
      setOutputTypeModal(emptyRefObj);
    }
  }, [showPointLevelResultsModal]);
  const alertBellTrigger = useRef(null);
  const listFilters = useRef(null);
  useEffect(() => {
    setTutorialPortfolioSchema(tutorialPortfolioSchema);
  }, [currentUser]);
  useEffect(() => {
    if (!isTutorialOpen) {
      setTutorialPortfolioSchema(initialTutorialPortfolioSchema);
      setIsTutorialNewRunModalOpen(false);
      setTutorialNewRunState("INITIAL");
      setShowDownloadDropdown(false);
      setShowSummaryModal(false);
      setShowMoreDropdown(false);
      setShowTutorialDeleteModal(false);
      setShowTutorialEditModal(false);
    }
  }, [isTutorialOpen]);
  const newRunModalTypeSelectionRef = useRef(null);
  const [newRunModalTypeSelection, setNewRunModalTypeSelection] = useState(emptyRefObj);
  const initNewRunTypeSelection = useDebouncedCallback(() => setNewRunModalTypeSelection(newRunModalTypeSelectionRef), {
    delay: 300,
    flushOnUnmount: true
  });
  const [newRunModalUpload, setNewRunModalUpload] = useState({
    uploadNewRun: emptyRefObj,
    customiseTrigger: emptyRefObj,
    csvUpload: emptyRefObj
  });
  const uploadNewRunRef = useRef(null);
  const customiseTriggerRef = useRef(null);
  const csvUploadRef = useRef(null);
  const initNewRunUpload = useDebouncedCallback(() => {
    setNewRunModalUpload({
      uploadNewRun: uploadNewRunRef,
      customiseTrigger: customiseTriggerRef,
      csvUpload: csvUploadRef
    });
  }, {
    delay: 300,
    flushOnUnmount: true
  });
  const [newRunModalReview, setNewRunModalReview] = useState({
    info: emptyRefObj,
    advancedSettings: emptyRefObj,
    usage: emptyRefObj,
    runAnalysisTrigger: emptyRefObj
  });
  const infoRef = useRef(null);
  const advancedSettingsRef = useRef(null);
  const usageRef = useRef(null);
  const runAnalysisTriggerRef = useRef(null);
  const initNewRunReview = useDebouncedCallback(() => {
    setNewRunModalReview({
      info: infoRef,
      advancedSettings: advancedSettingsRef,
      usage: usageRef,
      runAnalysisTrigger: runAnalysisTriggerRef
    });
  }, {
    delay: 300,
    flushOnUnmount: true
  });
  const [analysisRun, setAnalysisRun] = useState({
    alert: emptyRefObj,
    item: emptyRefObj
  });
  const analysisRunAlertRef = useRef(null);
  const analysisRunItemRef = useRef(null);
  const initAnalysisRun = useDebouncedCallback(() => {
    setAnalysisRun({
      alert: analysisRunAlertRef,
      item: analysisRunItemRef
    });
  }, {
    delay: 300,
    flushOnUnmount: true
  });
  useEffect(() => {
    if (tutorialNewRunState === "TYPE_SELECTION") {
      initNewRunTypeSelection();
    } else if (tutorialNewRunState === "INITIAL") {
      initNewRunUpload();
    } else if (tutorialNewRunState === "IN_REVIEW") {
      initNewRunReview();
    } else if (tutorialNewRunState === "UPLOADING") {
      initAnalysisRun();
    }
  }, [isTutorialNewRunModalOpen, tutorialNewRunState]);
  useEffect(() => {
    if (tutorialPortfolioSchema.status === "completed") initPortfolioItem();
  }, [isTutorialNewRunModalOpen, tutorialPortfolioSchema]);
  const summaryQueueModalRef = useRef(null);
  const [summaryQueueModal, setSummaryQueueModal] = useState(summaryQueueModalRef);
  const initSummaryQueueModal = useDebouncedCallback(() => {
    setSummaryQueueModal(summaryQueueModalRef);
  }, {
    delay: 300,
    flushOnUnmount: true
  });
  useEffect(() => {
    if (showSummaryModal) {
      initSummaryQueueModal();
    }
  }, [showSummaryModal]);
  const handleSummaryStep = () => {
    notification.open({
      key: "tutorial-summary",
      duration: 0,
      message: <div className="flex gap-2">
          <CheckCircleOutlined />
          <span>Summary Generated</span>
        </div>,
      description: <div className="text-sm">
          Your summary &quot;<em>{tutorialPortfolioSchema.name}</em>&quot; is ready to download.
        </div>
    });
    if (showAdaptationSummary) {
      notification.open({
        key: "tutorial-adaptation-summary",
        duration: 0,
        message: <div className="flex gap-2">
            <CheckCircleOutlined />
            <span>{terms("adaptationTypes.enhanced_resilience")} Summary Generated</span>
          </div>,
        description: <div className="text-sm">
            Your <span className="lowercase">{terms("adaptationTypes.enhanced_resilience")}</span> summary &quot;
            <em>{tutorialPortfolioSchema.name}</em>&quot; is ready to download.
          </div>
      });
    }
  };
  const handleSummaryGenerated = () => {
    notification.destroy();
  };
  return <PortfolioTutorialContext.Provider value={{
    isTutorialOpen,
    canRunAutoAdaptation,
    isTutorialNewRunModalOpen,
    setIsTutorialNewRunModalOpen,
    tutorialNewRunState,
    setTutorialNewRunState,
    tutorialPortfolioSchema,
    setTutorialPortfolioSchema,
    setTutorialSchemaForStep,
    newRunModalTrigger,
    newRunModalTypeSelection,
    newRunModalUpload,
    newRunModalReview,
    analysisRun,
    portfolioItem,
    summaryQueueModal,
    setShowSummaryModal,
    handleSummaryStep,
    handleSummaryGenerated,
    showDownloadDropdown,
    setShowDownloadDropdown,
    selectedDownloadItemIndex,
    setSelectedDownloadItemIndex,
    downloadsMenuPopup,
    showPointLevelResultsModal,
    setShowPointLevelResultsModal,
    showMoreDropdown,
    setShowMoreDropdown,
    editModal,
    deleteModal,
    showTutorialEditModal,
    setShowTutorialEditModal,
    showTutorialDeleteModal,
    setShowTutorialDeleteModal,
    outputTypeModal,
    alertBellTrigger,
    listFilters
  }} data-sentry-element="unknown" data-sentry-component="PortfolioTutorialProvider" data-sentry-source-file="PortfolioTutorialContext.tsx">
      {children}
      {showSummaryModal && <SummaryStatusModal ref={summaryQueueModal} summaries={tutorialSummaries} adaptationSummaries={tutorialAdaptationSummaries} defaultOpen />}

      {isTutorialOpen && showTutorialEditModal && <AnalysisCollectionEditModal open={showTutorialEditModal} isOwner={currentUser?.is_owner ?? false} schema={tutorialPortfolioSchema} onCancel={() => {}} onOk={() => Promise.resolve()} hasOrgKeys={!!currentUser?.has_org_keys} editModalRef={editModal as MutableRefObject<null>} />}
      {isTutorialOpen && showTutorialDeleteModal && <Modal modalRender={(node: ReactNode) => <div ref={deleteModal}>{node}</div>} open={showTutorialDeleteModal} title="Are you sure?" okText="Delete" cancelText="Cancel">
          <p>
            Deleting this item is permanent and will affect all members of your organisation. Would you like to proceed?
          </p>
        </Modal>}
      {isTutorialOpen && showPointLevelResultsModal && <PortfolioExportAssetLevelDownloadButton schema={tutorialPortfolioSchema} isTutorialOpen={isTutorialOpen} showPointLevelResultsModal={showPointLevelResultsModal} outputTypeModalRef={outputTypeModal} />}
    </PortfolioTutorialContext.Provider>;
};
export const usePortfolioTutorialsState = () => {
  const context = useContext(PortfolioTutorialContext);
  if (context === undefined) {
    return {
      isTutorialOpen: false,
      canRunAutoAdaptation: false,
      isTutorialNewRunModalOpen: false,
      setIsTutorialNewRunModalOpen: () => {},
      tutorialNewRunState: "INITIAL" as PortfolioNewRunModalStatus,
      setTutorialNewRunState: () => {},
      tutorialPortfolioSchema: {} as PortfolioExportJobSchema,
      setTutorialPortfolioSchema: () => {},
      setTutorialSchemaForStep: () => {},
      newRunModalTrigger: emptyRefObj,
      newRunModalTypeSelection: emptyRefObj,
      newRunModalUpload: {
        uploadNewRun: emptyRefObj,
        customiseTrigger: emptyRefObj,
        csvUpload: emptyRefObj
      },
      newRunModalReview: {
        info: emptyRefObj,
        advancedSettings: emptyRefObj,
        usage: emptyRefObj,
        runAnalysisTrigger: emptyRefObj
      },
      analysisRun: {
        alert: emptyRefObj,
        item: emptyRefObj
      },
      portfolioItem: {
        actionsMenu: emptyRefObj,
        downloadsMenu: emptyRefObj,
        viewResultsTrigger: emptyRefObj
      },
      downloadsMenuPopup: emptyRefObj,
      outputTypeModal: emptyRefObj,
      editModal: emptyRefObj,
      deleteModal: emptyRefObj,
      showDownloadDropdown: false,
      setShowDownloadDropdown: () => {},
      selectedDownloadItemIndex: undefined,
      setSelectedDownloadItemIndex: () => {},
      setShowSummaryModal: () => {},
      handleSummaryStep: () => {},
      handleSummaryGenerated: () => {},
      summaryQueueModal: emptyRefObj,
      showPointLevelResultsModal: false,
      setShowPointLevelResultsModal: () => {},
      showMoreDropdown: false,
      setShowMoreDropdown: () => {},
      showTutorialEditModal: false,
      setShowTutorialEditModal: () => {},
      showTutorialDeleteModal: false,
      setShowTutorialDeleteModal: () => {},
      alertBellTrigger: emptyRefObj,
      listFilters: emptyRefObj
    } as PortfolioTutorialState;
  }
  return context;
};